/**
 * Shared utility functions for downloading files using presigned URLs
 */

/**
 * Check if the current platform is a mobile device
 * @returns {boolean} True if mobile device
 */
export const isMobilePlatform = () => {
  if (typeof window === 'undefined') return false;
  
  // Check user agent for mobile patterns
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
  return mobileRegex.test(userAgent);
};

/**
 * Get MIME type from filename extension
 * @param {string} filename - The filename
 * @returns {string} MIME type
 */
export const getMimeTypeFromFilename = (filename) => {
  const extension = filename.toLowerCase().substring(filename.lastIndexOf('.'));
  const mimeTypes = {
    // Images
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.png': 'image/png',
    '.gif': 'image/gif',
    '.webp': 'image/webp',
    '.bmp': 'image/bmp',
    '.svg': 'image/svg+xml',
    '.tiff': 'image/tiff',
    '.tif': 'image/tiff',
    // Videos
    '.mp4': 'video/mp4',
    '.mov': 'video/quicktime',
    '.avi': 'video/x-msvideo',
    '.mkv': 'video/x-matroska',
    '.webm': 'video/webm',
    '.m4v': 'video/x-m4v',
    '.3gp': 'video/3gpp',
    '.flv': 'video/x-flv',
    '.wmv': 'video/x-ms-wmv',
    '.qt': 'video/quicktime',
    '.mts': 'video/mp2t',
    '.m2ts': 'video/mp2t',
    '.ts': 'video/mp2t',
    '.vob': 'video/mpeg',
    '.ogv': 'video/ogg',
    '.mxf': 'application/mxf'
  };
  return mimeTypes[extension] || 'application/octet-stream';
};

/**
 * Download a single file using a presigned URL with Web Share API support
 * @param {string} url - Presigned URL for the file
 * @param {string} filename - Original filename for the download
 * @returns {Promise<void>}
 */
export const downloadFileFromUrl = async (url, filename) => {
  try {
    // Fetch the file as a blob
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
    }
    
    // Get Content-Type from response headers for accurate MIME type
    const contentType = response.headers.get('content-type') || null;
    const blob = await response.blob();
    
    // If blob doesn't have type but we got it from headers, create a new blob with the type
    let typedBlob = blob;
    if (contentType && (!blob.type || blob.type === 'application/octet-stream')) {
      typedBlob = new Blob([blob], { type: contentType });
    }
    
    // Try Web Share API first (mobile-only)
    const isMobile = isMobilePlatform();
    const canNativeShareFiles = typeof navigator !== 'undefined' &&
      typeof navigator.canShare === 'function' &&
      typeof navigator.share === 'function';
    
    const WEB_SHARE_MAX_SIZE = 40 * 1024 * 1024; // 40MB
    const isSizeTooLarge = blob.size > WEB_SHARE_MAX_SIZE;
    
    if (isMobile && canNativeShareFiles && !isSizeTooLarge) {
      try {
        // Ensure filename has proper extension
        let safeFilename = filename;
        if (!safeFilename.includes('.')) {
          // If no extension, try to determine from blob type or content type
          const detectedType = contentType || blob.type || '';
          if (detectedType.startsWith('image/')) {
            safeFilename = `${safeFilename}.jpg`;
          } else if (detectedType.startsWith('video/')) {
            safeFilename = `${safeFilename}.mp4`;
          }
        }
        
        // Get MIME type: prefer content-type from response, then from filename, then blob type
        const mimeType = contentType || getMimeTypeFromFilename(safeFilename) || blob.type || 'application/octet-stream';
        
        const file = new File([typedBlob], safeFilename, { type: mimeType });
        
        if (navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({
              title: 'Wedding Photo',
              text: 'Wedding photo',
              files: [file]
            });
            return; // Successfully shared, exit
          } catch (err) {
            // Do not fall back to download for user cancellations or permission denials
            const name = err?.name;
            const message = err?.message || '';
            
            // Check if it's a technical limitation vs user cancellation
            const isTechnicalError = message.toLowerCase().includes('too large') || 
                                     message.toLowerCase().includes('share too large') ||
                                     message.toLowerCase().includes('exceeded') ||
                                     message.toLowerCase().includes('limit') ||
                                     message.toLowerCase().includes('size');
            
            // Only exit for true user cancellations, not technical limitations
            if (name === 'AbortError' || name === 'SecurityError') {
              if (!isTechnicalError) {
                return; // User cancelled, exit without downloading
              }
            }
            
            // For NotAllowedError, check if it's a technical issue or user denial
            if (name === 'NotAllowedError' && !isTechnicalError) {
              return; // User denied permission, exit without downloading
            }
            
            // For technical errors, continue to download fallback below
          }
        }
      } catch (error) {
        console.warn('Error preparing file for Web Share API:', error);
        // Continue to download fallback
      }
    }
    
    // Fallback to direct download
    const blobUrl = window.URL.createObjectURL(typedBlob);
    
    // Create a temporary anchor element and trigger download
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Download failed:', error);
    // Fallback: try direct download from URL
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    throw error;
  }
};

/**
 * Fetch a single file from a presigned URL
 * @param {string} fileName - The filename
 * @param {string} url - Presigned URL
 * @returns {Promise<{filename: string, blob: Blob, contentType: string} | null>}
 */
const fetchSingleFile = async (fileName, url) => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch ${fileName}: ${response.status} ${response.statusText}`);
    }
    // Get Content-Type from response headers for accurate MIME type
    const contentType = response.headers.get('content-type') || null;
    const blob = await response.blob();
    
    // If blob doesn't have type but we got it from headers, create a new blob with the type
    let typedBlob = blob;
    if (contentType && (!blob.type || blob.type === 'application/octet-stream')) {
      typedBlob = new Blob([blob], { type: contentType });
    }
    
    return { filename: fileName, blob: typedBlob, contentType };
  } catch (error) {
    console.error(`Failed to fetch ${fileName}:`, error);
    return null;
  }
};

/**
 * Process items in batches with controlled concurrency
 * @param {Array} items - Items to process
 * @param {number} concurrency - Maximum concurrent operations
 * @param {Function} processor - Async function to process each item
 * @param {Function} onProgress - Optional callback for progress updates (current, total)
 * @returns {Promise<Array>} Results array
 */
const processWithConcurrency = async (items, concurrency, processor, onProgress) => {
  const results = [];
  const total = items.length;
  
  // Process items in batches
  for (let i = 0; i < items.length; i += concurrency) {
    const batch = items.slice(i, i + concurrency);
    const batchPromises = batch.map(item => processor(item));
    const batchResults = await Promise.all(batchPromises);
    
    results.push(...batchResults);
    
    // Report progress if callback provided
    if (onProgress) {
      onProgress(results.length, total);
    }
  }
  
  return results;
};

/**
 * Fetch multiple files using presigned URLs and return blobs
 * Uses controlled concurrency to avoid overwhelming browser connections
 * @param {Array<{fileName: string, url: string}>} urlData - Array of objects with fileName and url
 * @param {Object} options - Configuration options
 * @param {number} options.concurrency - Maximum concurrent fetches (default: 6, respects browser limits)
 * @param {Function} options.onProgress - Optional callback for progress (current, total)
 * @returns {Promise<Array<{filename: string, blob: Blob, contentType: string}>>}
 */
export const fetchFilesFromUrls = async (urlData, options = {}) => {
  const { concurrency = 6, onProgress } = options;
  
  // Filter valid URLs
  const validUrls = urlData.filter(({ url, error }) => url && !error);
  
  if (validUrls.length === 0) {
    return [];
  }
  
  // Process with controlled concurrency
  const results = await processWithConcurrency(
    validUrls,
    concurrency,
    ({ fileName, url }) => fetchSingleFile(fileName, url),
    onProgress
  );
  
  // Filter out null results and ensure blobs are valid
  return results.filter((r) => r && r.blob && r.blob.size > 0);
};

